import { useEffect, useRef } from 'react'
import { useFocusTrap } from '../hooks/useFocusTrap'
import { Kbd } from './Kbd'

interface ShortcutsHelpPanelProps {
  onClose: () => void
}

const SECTIONS: { title: string; items: { label: string; keys: string[] }[] }[] = [
  {
    title: 'Navigation',
    items: [
      { label: 'Next conversation', keys: ['J'] },
      { label: 'Previous conversation', keys: ['K'] },
      { label: 'Open conversation', keys: ['Enter'] },
      { label: 'Go back', keys: ['Esc'] },
    ],
  },
  {
    title: 'Selection',
    items: [
      { label: 'Toggle selection', keys: ['X'] },
      { label: 'Extend selection down', keys: ['Shift', 'J'] },
      { label: 'Extend selection up', keys: ['Shift', 'K'] },
      { label: 'Select all (list)', keys: ['Cmd', 'A'] },
      { label: 'Copy conversation (thread)', keys: ['Cmd', 'A'] },
      { label: 'Clear selection', keys: ['Esc'] },
    ],
  },
  {
    title: 'Actions',
    items: [
      { label: 'Archive', keys: ['E'] },
      { label: 'Unarchive', keys: ['Shift', 'E'] },
      { label: 'Toggle read/unread', keys: ['U'] },
      { label: 'Star/bookmark', keys: ['S'] },
      { label: 'Remind me', keys: ['H'] },
      { label: 'Tag conversation', keys: ['T'] },
      { label: 'Organize/label', keys: ['L'] },
      { label: 'Command palette', keys: ['/'] },
    ],
  },
  {
    title: 'Inbox views',
    items: [
      { label: 'All excluding archived', keys: ['Shift', 'A'] },
      { label: 'Waiting for their reply', keys: ['Shift', 'S'] },
      { label: 'Waiting for my reply', keys: ['Shift', 'W'] },
      { label: 'Unread', keys: ['Shift', 'Q'] },
    ],
  },
  {
    title: 'Compose',
    items: [
      { label: 'Reply', keys: ['R'] },
      { label: 'New message', keys: ['C'] },
      { label: 'Send message', keys: ['\u2318', 'Enter'] },
    ],
  },
  {
    title: 'Other',
    items: [
      { label: 'Show shortcuts', keys: ['?'] },
    ],
  },
]

export default function ShortcutsHelpPanel({ onClose }: ShortcutsHelpPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div
      className="animate-fade-in-fast fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcuts-help-title"
    >
      <div
        ref={panelRef}
        className="animate-scale-in w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 id="shortcuts-help-title" className="text-sm font-semibold text-zinc-900">Keyboard shortcuts</h2>
          <button
            onClick={onClose}
            aria-label="Close shortcuts"
            className="flex h-6 w-6 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="space-y-5">
          {SECTIONS.map((section) => (
            <div key={section.title}>
              <h3 className="mb-2 text-[10px] font-medium tracking-wider text-zinc-400">
                {section.title.toUpperCase()}
              </h3>
              <div className="space-y-1">
                {section.items.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-center justify-between rounded-md px-2 py-1.5"
                  >
                    <span className="text-xs text-zinc-600">{item.label}</span>
                    <div className="flex items-center gap-1">
                      {item.keys.map((key, i) => (
                        <Kbd key={`${key}-${i}`}>{key}</Kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
